import { format as formatDate } from 'common/lib/dates';

import getToken from '../auth';
import { defaultLang } from '../i18n';
import { get } from '../utils/api';
import update, { get as getState } from '../state';

const PERIOD_MONTHLY = 'monthly';
const PERIOD_WEEKLY = 'weekly';

const MAX_GROUPS = {
  [PERIOD_MONTHLY]: 12,
  [PERIOD_WEEKLY]: 36,
};

const groupStatsByTime = (stats, period = PERIOD_MONTHLY) => {
  const groupDateFormat = period === 'monthly' ? 'yyyy-MM' : 'yyyy-w';
  const createStatsObject = (date) => ({
    date,
    group: formatDate(date, groupDateFormat, {
      locale: defaultLang,
    }),
    added: 0,
    unsubscribed: 0,
    bounced: 0,
    deleted: 0,
    sum: 0,
  });
  const monthMatch = (a, b) =>
    a.date.getFullYear() === b.date.getFullYear() &&
    a.date.getMonth() === b.date.getMonth();
  const weekMatch = (a, b) =>
    a.week === b.week &&
    (a.date.getFullYear() === b.date.getFullYear() ||
      (a.date.getMonth() === 11 && b.date.getMonth() === 0));

  const groupedStats = stats.reduce(
    (grouped, current, i) => {
      let statsObject = grouped[grouped.length - 1];

      if (
        i > 0 &&
        ((period === PERIOD_MONTHLY && !monthMatch(stats[i - 1], current)) ||
          (period === PERIOD_WEEKLY && !weekMatch(stats[i - 1], current)))
      ) {
        statsObject = createStatsObject(current.date);
        grouped.push(statsObject);
      }

      Object.keys(current).forEach((key) => {
        if (key === 'date' || key === 'week') return;

        statsObject[key] += current[key];
      });

      return grouped;
    },
    [createStatsObject(stats[0].date)]
  );

  if (MAX_GROUPS[period] && groupedStats.length > MAX_GROUPS[period]) {
    return groupedStats.slice(groupedStats.length - MAX_GROUPS[period]);
  }

  return groupedStats;
};

const loadContactsStats = () =>
  new Promise((resolve, reject) => {
    getToken('contacts')
      .then((token) => get('/contacts/stats/', { token }))
      .then((response) => {
        const fakeData = generateFakeData(response);
        if (response.length) {
          // Deserialize date and annotate with week number too
          response.forEach((day) => {
            const datetime = new Date(day.date);

            Object.assign(day, {
              date: datetime,
              week: formatDate(datetime, 'w', {
                locale: defaultLang,
              }),
            });
          });

          update(['statistics', 'contacts'], [...fakeData, ...response]);
        }

        resolve([...fakeData, ...response]);
      })
      .catch(reject);
  });

/**
 * Return a list from API.
 *
 * @param {Int} id
 * @return {Object}
 */
const loadList = (id) => {
  return new Promise((resolve) => {
    const loadedList = getState(['lists', id]);

    if (loadedList) {
      resolve(loadedList);
    } else {
      getToken('lists').then((token) => {
        get(`/lists/${id}/`, { token }).then((list) => {
          update(['lists', list.id], list);
          resolve(list);
        });
      });
    }
  });
};

/**
 * Generates fake dummy data for a specified number of days
 * before the provided data.
 *
 * @param {Array} existingData The provided data array representing
 *                             existing data.
 * @param {number} daysToGenerate The total number of days for which
 *                                to generate fake data.
 * @returns {Array} An array containing fake dummy data for the
 *                  specified number of days.
 */
const generateFakeData = (existingData, daysToGenerate = 56) => {
  const firstDay = new Date(existingData[0].date);
  const lastDay = new Date(existingData[existingData.length - 1].date);
  const firstWeek = parseInt(formatDate(firstDay, 'w', { locale: defaultLang }), 10);
  const lastWeek = parseInt(formatDate(lastDay, 'w', { locale: defaultLang }), 10);
  const numWeeks = lastWeek - firstWeek + 1;

  const totalFakeDays = daysToGenerate - 7 * numWeeks;

  if (totalFakeDays <= 0) {
    return [];
  }

  const endDate = new Date(existingData[0].date);
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - totalFakeDays);

  const dummyData = [];

  const currentDate = new Date(startDate);
  while (currentDate < endDate) {
    dummyData.push({
      added: 0,
      bounced: 0,
      date: new Date(currentDate),
      week: formatDate(currentDate, 'w', {
        locale: defaultLang,
      }),
      deleted: 0,
      sum: 0,
      unsubscribed: 0,
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dummyData;
};

export { PERIOD_MONTHLY, PERIOD_WEEKLY, groupStatsByTime, loadContactsStats, loadList };
